import type { Condition, SearchQueryBody } from "@/types";

import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";
import type { UserRecord } from "../users/UserRecord";

import type {
  BackfillOrgModelConfigsResponse,
  BedrockCatalogModel,
  BedrockModelRequest,
  BedrockModelRole,
  BedrockModelRoleBinding,
  BulkOrgModelConfigRequest,
  BulkOrgModelConfigResponse,
  OrgLlmConnectionRecord,
  OrgLlmConnectionResponse,
  OrgLlmProviderTemplatesResponse,
  OrgModelConfigFilter,
  OrgModelListResponse,
  SearchOrgModelConfigsRequest,
  SearchOrgModelConfigsResponse,
  UpdateOrgModelListRequest,
  UpsertOrgLlmConnectionRequest,
  VerifyOrgLlmConnectionRequest,
  VerifyOrgLlmConnectionResponse,
  VerifyOrgModelResponse,
} from "./OrgLlmProviderRecord";
import {
  EventModel,
  OrgRecord,
  OrganizationTier,
  RobotoRegion,
} from "./OrgRecord";

export interface OrgUserRecord {
  user: UserRecord;
  org: OrgRecord;
  roles: string[];
}

interface Options {
  abortSignal: AbortSignal;
  searchParams: URLSearchParams;
}

export class OrgService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getAvailableDataRegions(
    options?: Partial<Options>,
  ): Promise<RobotoRegion[]> {
    const requestUrl = this.#httpClient.constructUrl(`v1/integrations/regions`);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<RobotoRegion[]>();
  }

  public async getOrg(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<OrgRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/orgs`);
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        orgId: orgId,
      }),
    });
    return response.json<OrgRecord>();
  }

  public async getAllOrgs(options?: Partial<Options>): Promise<OrgRecord[]> {
    const orgs = [];
    let pageToken = null;

    while (true) {
      const searchParams = new URLSearchParams();
      if (pageToken) {
        searchParams.append("page_token", pageToken);
      }

      const url = this.#httpClient.constructUrl("v1/orgs/all", searchParams);
      const response = await this.#httpClient.get(url, {
        signal: options?.abortSignal,
      });

      const paginatedResponse =
        await response.json<PaginatedResponse<OrgRecord>>();
      orgs.push(...paginatedResponse.items);
      pageToken = paginatedResponse.next_token;
      if (!pageToken) {
        break;
      }
    }

    return orgs;
  }

  /**
   * One-page org search for autocompletes. A thin wrapper over
   * {@link listOrgs} that returns only the first page's items.
   */
  public async searchOrgs(
    search: string,
    options?: Partial<Options>,
  ): Promise<OrgRecord[]> {
    const page = await this.listOrgs({ search, limit: 50 }, options);
    return page.items;
  }

  /**
   * Admin-only lookup of an exact set of orgs, in one request: an OR of
   * EQUALS conditions on `org_id` POSTed to `v1/orgs/search`, with `limit`
   * sized to the set so every match comes back in one page. The endpoint caps
   * `limit` at 200, so callers bound the set. Ids that match no org are simply
   * absent from the result; an empty set sends no request.
   */
  public async getOrgsByIds(
    orgIds: string[],
    options?: Partial<Options>,
  ): Promise<OrgRecord[]> {
    if (orgIds.length === 0) {
      return [];
    }
    const query: SearchQueryBody = {
      condition: {
        operator: "OR",
        conditions: orgIds.map((orgId) => ({
          field: "org_id",
          comparator: "EQUALS",
          value: orgId,
        })),
      },
      limit: orgIds.length,
    };
    const url = this.#httpClient.constructUrl("v1/orgs/search");
    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(query),
      signal: options?.abortSignal,
    });
    const page = await response.json<PaginatedResponse<OrgRecord>>();
    return page.items;
  }

  /**
   * Admin-only paginated org search, POSTed to `v1/orgs/search` as a
   * `SearchQueryBody` like every `/search` endpoint. This method is the single
   * place the picker-style params become a structured body: `search` matches
   * `name` or `org_id` as an OR of CONTAINS conditions, `tier` becomes an
   * EQUALS condition AND-ed on, `pageToken` rides in `after`. The backend
   * whitelists condition fields (`org_id`, `name`, `tier`) and `sort_by`
   * (`created`/`name`/`org_id`, default `created` descending), and returns a
   * `total_count` alongside each page.
   */
  public async listOrgs(
    params: {
      search?: string;
      tier?: OrganizationTier;
      pageToken?: string;
      limit?: number;
    },
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<OrgRecord>> {
    const conditions: Condition[] = [];
    if (params.search && params.search.length > 0) {
      conditions.push({
        operator: "OR",
        conditions: [
          { field: "name", comparator: "CONTAINS", value: params.search },
          { field: "org_id", comparator: "CONTAINS", value: params.search },
        ],
      });
    }
    if (params.tier !== undefined) {
      conditions.push({
        field: "tier",
        comparator: "EQUALS",
        value: params.tier,
      });
    }
    let condition: Condition | undefined;
    if (conditions.length === 1) {
      condition = conditions[0];
    } else if (conditions.length > 1) {
      condition = { operator: "AND", conditions };
    }

    const query: SearchQueryBody = {
      ...(condition && { condition }),
      ...(params.pageToken && { after: params.pageToken }),
      ...(params.limit !== undefined && { limit: params.limit }),
    };

    const url = this.#httpClient.constructUrl("v1/orgs/search");
    const response = await this.#httpClient.post(url, {
      body: JSON.stringify(query),
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<OrgRecord>>();
  }

  public async getOrgUsers(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<OrgUserRecord[]> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/users`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<OrgUserRecord[]>();
  }

  /**
   * Structured search over one org's members, paginated.
   *
   * The bounded counterpart to {@link getOrgUsers}, which returns the whole
   * membership — with its org and role rows — in one unpaginated response.
   * Prefer this for member pickers: records carry only `user_id` and `name`
   * (the two fields a picker row renders), and `limit` caps the page so a large
   * org doesn't have to be pulled in full before the list can be shown.
   *
   * POSTs a `SearchQueryBody` to `v1/orgs/id/<org_id>/users/search`, like every
   * `/search` endpoint. The backend whitelists condition fields and `sort_by`
   * to `user_id` and `name` (default `user_id` ascending); a picker's typed
   * term goes in as an OR of two CONTAINS conditions. Omit `condition` for the
   * on-open page. A response with no `next_token` is the complete match set,
   * which is what lets a picker filter it client-side instead of re-querying
   * per keystroke.
   */
  public async searchOrgUsers(
    orgId: string,
    query: SearchQueryBody,
    options?: Partial<Options>,
  ): Promise<PaginatedResponse<UserRecord>> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/users/search`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      body: JSON.stringify(query),
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<UserRecord>>();
  }

  public async updateOrgTier(
    orgId: string,
    tier: OrganizationTier,
    options?: Partial<Options>,
  ): Promise<OrgRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/tier`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      body: JSON.stringify({ tier: tier }),
    });
    return response.json<OrgRecord>();
  }

  /**
   * Move the org to `eventModel`. Roboto admins only. Models only move forward
   * (legacy, dual, migrating, session); the server refuses to go back.
   */
  public async updateOrgEventModel(
    orgId: string,
    eventModel: EventModel,
    options?: Partial<Options>,
  ): Promise<OrgRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/event-model`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      body: JSON.stringify({ event_model: eventModel }),
    });
    return response.json<OrgRecord>();
  }

  /** The org's chat model list and its own connections. Any member of the org may read them. */
  public async getOrgLlmProvider(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<OrgModelListResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-provider`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<OrgModelListResponse>();
  }

  /**
   * Replace the org's chat model list (org admins and Roboto admins). Unless
   * the org is premium, adding a model on an own connection, or moving one to
   * another connection, is refused with a 403. A list saved since
   * `request.expected_modified` is refused with a 409.
   */
  public async updateOrgLlmProvider(
    orgId: string,
    request: UpdateOrgModelListRequest,
    options?: Partial<Options>,
  ): Promise<OrgModelListResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-provider`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      // Not replayed: the server calls the provider before answering, so a 5xx is often that call timing out.
      idempotent: false,
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    return response.json<OrgModelListResponse>();
  }

  /**
   * Replace the org's list with the Roboto default models (org admins and
   * Roboto admins). The org's connections are kept.
   */
  public async resetOrgLlmProvider(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<OrgModelListResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-provider/reset`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
    });
    return response.json<OrgModelListResponse>();
  }

  /**
   * What the settings form pre-fills: the Roboto-managed models this caller may
   * put on the org, and the connection form defaults per kind.
   */
  public async getOrgLlmProviderTemplates(
    orgId: string,
    options?: Partial<Options>,
  ): Promise<OrgLlmProviderTemplatesResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-provider/templates`,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<OrgLlmProviderTemplatesResponse>();
  }

  /**
   * Add one of the org's own LLM connections (premium orgs; org admins and
   * Roboto admins). The server stores it only after a call to
   * `request.test_model_id` succeeds; otherwise it rejects with the provider's error.
   */
  public async createOrgLlmConnection(
    orgId: string,
    request: UpsertOrgLlmConnectionRequest,
    options?: Partial<Options>,
  ): Promise<OrgLlmConnectionRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-connections`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      // Not replayed: the server calls the provider before answering, so a 5xx is often that call timing out.
      idempotent: false,
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    const body = await response.json<OrgLlmConnectionResponse>();
    return body.connection;
  }

  /**
   * Replace a connection's label and settings (premium orgs). Changed settings
   * are verified before they are stored (with `request.test_model_id`, or a
   * switched-on, non-failing model on the org's list on this connection); a
   * label-only edit makes no call.
   */
  public async updateOrgLlmConnection(
    orgId: string,
    connectionId: string,
    request: UpsertOrgLlmConnectionRequest,
    options?: Partial<Options>,
  ): Promise<OrgLlmConnectionRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-connections/${encodeURIComponent(connectionId)}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      // Not replayed: the server calls the provider before answering, so a 5xx is often that call timing out.
      idempotent: false,
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    const body = await response.json<OrgLlmConnectionResponse>();
    return body.connection;
  }

  /**
   * Call a model on the connection and record the outcome (premium orgs,
   * admins only). A failed call still resolves: the connection comes back
   * `failing` and `error` carries the provider's message; a success sets it
   * back to `active`. Without `testModelId` the server calls the first
   * switched-on, non-failing model on the org's list on this connection, and
   * answers 400 when there is none.
   */
  public async verifyOrgLlmConnection(
    orgId: string,
    connectionId: string,
    testModelId?: string | null,
    options?: Partial<Options>,
  ): Promise<VerifyOrgLlmConnectionResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-connections/${encodeURIComponent(connectionId)}/verify`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      // Not replayed: the server calls the provider before answering, so a 5xx is often that call timing out.
      idempotent: false,
      signal: options?.abortSignal,
      body: JSON.stringify({
        test_model_id: testModelId ?? null,
      } satisfies VerifyOrgLlmConnectionRequest),
    });
    return response.json<VerifyOrgLlmConnectionResponse>();
  }

  /**
   * Call one of the org's own-connection models and record the outcome on it
   * (premium orgs, admins only). A failed call still resolves, with `error`
   * set and the model's `last_error` recorded.
   */
  public async verifyOrgLlmModel(
    orgId: string,
    profileId: string,
    options?: Partial<Options>,
  ): Promise<VerifyOrgModelResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-provider/models/${encodeURIComponent(profileId)}/verify`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      // Not replayed: the server calls the provider before answering, so a 5xx is often that call timing out.
      idempotent: false,
      signal: options?.abortSignal,
    });
    return response.json<VerifyOrgModelResponse>();
  }

  /** Delete a connection no model on the org's list uses (any tier). */
  public async deleteOrgLlmConnection(
    orgId: string,
    connectionId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/id/${orgId}/llm-connections/${encodeURIComponent(connectionId)}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
    });
    return response.throwIfError();
  }

  /** Every model in the deployment's Bedrock catalog, `catalog_only` ones included (Roboto admins only). */
  public async getBedrockCatalog(
    options?: Partial<Options>,
  ): Promise<BedrockCatalogModel[]> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/orgs/llm-providers/bedrock-catalog",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<BedrockCatalogModel[]>();
  }

  /** One page of every org's effective model configuration (Roboto admins only). */
  public async searchOrgModelConfigs(
    request: SearchOrgModelConfigsRequest,
    options?: Partial<Options>,
  ): Promise<SearchOrgModelConfigsResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/orgs/llm-providers/search",
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    return response.json<SearchOrgModelConfigsResponse>();
  }

  /** The id of every org matching `filter`, unpaginated, for "select all matching" (Roboto admins only). */
  public async searchOrgModelConfigIds(
    filter: OrgModelConfigFilter,
    options?: Partial<Options>,
  ): Promise<string[]> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/orgs/llm-providers/search-ids",
    );
    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(filter),
    });
    const body = await response.json<{ org_ids: string[] }>();
    return body.org_ids;
  }

  /**
   * Apply one model-configuration action to many orgs in a single
   * transaction (Roboto admins only). With `dry_run`, returns what would be
   * updated and skipped without writing.
   */
  public async bulkUpdateOrgModelConfigs(
    request: BulkOrgModelConfigRequest,
    options?: Partial<Options>,
  ): Promise<BulkOrgModelConfigResponse> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/orgs/llm-providers/bulk",
    );
    const response = await this.#httpClient.post(requestUrl, {
      // A write retried after it landed would report every org as already
      // in the requested state; one attempt keeps the reported counts true.
      idempotent: request.dry_run === true,
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    return response.json<BulkOrgModelConfigResponse>();
  }

  /**
   * Give every org that has no configuration row the Roboto default Bedrock
   * row (Roboto admins only). Idempotent; resolves to how many rows it made.
   */
  public async backfillOrgModelConfigs(
    options?: Partial<Options>,
  ): Promise<number> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/orgs/llm-providers/backfill",
    );
    const response = await this.#httpClient.post(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
    });
    const body = await response.json<BackfillOrgModelConfigsResponse>();
    return body.created;
  }

  /**
   * Add a model to the Bedrock catalog (Roboto admins only). The server
   * makes a test call first and refuses a model that fails it; one that
   * passes is stored verified.
   */
  public async createBedrockModel(
    request: BedrockModelRequest,
    options?: Partial<Options>,
  ): Promise<BedrockCatalogModel> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/orgs/llm-providers/bedrock-catalog",
    );
    const response = await this.#httpClient.post(requestUrl, {
      // Create is not idempotent: a retry after it landed fails as "id taken".
      idempotent: false,
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    return response.json<BedrockCatalogModel>();
  }

  /**
   * Replace a catalog model's fields (Roboto admins only). The id in the
   * path wins. A changed `model_id` or `capability_overrides` is saved only
   * once the server's test call with the new values passes.
   */
  public async updateBedrockModel(
    modelId: string,
    request: BedrockModelRequest,
    options?: Partial<Options>,
  ): Promise<BedrockCatalogModel> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/llm-providers/bedrock-catalog/${encodeURIComponent(modelId)}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });
    return response.json<BedrockCatalogModel>();
  }

  /**
   * Make a one-token Bedrock call with the model and record the outcome
   * (Roboto admins only). A failed call still resolves: the returned model
   * has `verified: false` and `last_error` set.
   */
  public async verifyBedrockModel(
    modelId: string,
    options?: Partial<Options>,
  ): Promise<BedrockCatalogModel> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/llm-providers/bedrock-catalog/${encodeURIComponent(modelId)}/verify`,
    );
    const response = await this.#httpClient.post(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
    });
    return response.json<BedrockCatalogModel>();
  }

  /** Remove a model from the catalog; refused for the default or a model any org uses (Roboto admins only). */
  public async deleteBedrockModel(
    modelId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/llm-providers/bedrock-catalog/${encodeURIComponent(modelId)}`,
    );
    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.throwIfError();
  }

  /** Which catalog entry does each deployment-wide model role, in a fixed order (Roboto admins only). */
  public async getBedrockRoles(
    options?: Partial<Options>,
  ): Promise<BedrockModelRoleBinding[]> {
    const requestUrl = this.#httpClient.constructUrl(
      "v1/orgs/llm-providers/bedrock-roles",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
    });
    return response.json<BedrockModelRoleBinding[]>();
  }

  /**
   * Bind a role to a verified catalog entry, or unset an optional role with
   * `null` (Roboto admins only).
   */
  public async updateBedrockRole(
    role: BedrockModelRole,
    model: string | null,
    options?: Partial<Options>,
  ): Promise<BedrockModelRoleBinding> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/orgs/llm-providers/bedrock-roles/${role}`,
    );
    const response = await this.#httpClient.put(requestUrl, {
      idempotent: true,
      signal: options?.abortSignal,
      body: JSON.stringify({ model }),
    });
    return response.json<BedrockModelRoleBinding>();
  }
}
